import React, { useEffect, useRef, useState } from 'react';

/**
 * Wraps a horizontally-scrollable content with TWO synchronized scrollbars:
 * - Native bottom scrollbar of the content
 * - Floating bottom scrollbar fixed to the viewport, visible whenever the
 *   table is larger than the screen and the user has not yet scrolled past
 *   the bottom of the content.
 */
export const DoubleScroll = ({ children, className = '' }) => {
  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const floatingRef = useRef(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [floatingVisible, setFloatingVisible] = useState(false);
  const [floatingLeft, setFloatingLeft] = useState(0);
  const [floatingWidth, setFloatingWidth] = useState(0);

  // Measure inner content width
  useEffect(() => {
    const measure = () => {
      if (innerRef.current) {
        setContentWidth(innerRef.current.scrollWidth);
      }
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setFloatingLeft(rect.left);
        setFloatingWidth(rect.width);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (innerRef.current) ro.observe(innerRef.current);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      ro.disconnect();
    };
  });

  // Detect if floating scrollbar should be visible: the table's native bottom
  // edge is below the viewport (so the user can't see/use the native bar).
  useEffect(() => {
    const checkVisibility = () => {
      if (!containerRef.current || !innerRef.current) return;
      const overflowing = innerRef.current.scrollWidth > containerRef.current.clientWidth + 1;
      if (!overflowing) { setFloatingVisible(false); return; }
      const rect = containerRef.current.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // Show floating bar when the content's bottom is BELOW the viewport
      const isBelowViewport = rect.bottom > vh;
      // And when at least part of the content is in the viewport
      const isInView = rect.top < vh && rect.bottom > 0;
      setFloatingVisible(isBelowViewport && isInView);
    };
    checkVisibility();
    window.addEventListener('scroll', checkVisibility, true);
    window.addEventListener('resize', checkVisibility);
    return () => {
      window.removeEventListener('scroll', checkVisibility, true);
      window.removeEventListener('resize', checkVisibility);
    };
  });

  // Sync scroll between floating bar and content
  const onFloatingScroll = () => {
    if (containerRef.current && floatingRef.current) {
      containerRef.current.scrollLeft = floatingRef.current.scrollLeft;
    }
  };
  const onContentScroll = () => {
    if (containerRef.current && floatingRef.current) {
      floatingRef.current.scrollLeft = containerRef.current.scrollLeft;
    }
  };

  return (
    <div className={className}>
      <div
        ref={containerRef}
        onScroll={onContentScroll}
        className="overflow-x-auto"
      >
        <div ref={innerRef} style={{ display: 'inline-block', minWidth: '100%' }}>
          {children}
        </div>
      </div>
      {/* Floating scrollbar fixed at bottom of viewport while content overflows */}
      {floatingVisible && (
        <div
          ref={floatingRef}
          onScroll={onFloatingScroll}
          className="double-scroll-floating overflow-x-auto overflow-y-hidden border-t border-[#2D3E6F]/20 shadow-lg"
          style={{
            position: 'fixed',
            bottom: 0,
            left: floatingLeft,
            width: floatingWidth,
            height: 14,
            zIndex: 50,
            backgroundColor: 'rgba(45, 62, 111, 0.12)',
          }}
        >
          <div style={{ width: contentWidth, height: 1 }} />
        </div>
      )}
    </div>
  );
};

export default DoubleScroll;
